'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { createContato } from '@/services/contato/createContato';
import { IPAGEContacto } from '@/services/types/SingleTypes';
import HighlightedHeading from '@/components/molecules/HighlightedHeading';
import { getHighlightPositions } from '@/lib/utils';

// Dynamically import Leaflet only on client side
const LeafletMap = dynamic(() => import('./LeafletMap'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full flex items-center justify-center bg-gray-100">
      <div className="text-gray-600">Carregando mapa...</div>
    </div>
  ),
});

interface ContactFormData {
  name: string;
  apelido: string;
  email: string;
  telefone: string;
  assunto: string;
  mensagem: string;
}

interface ContactFormProps {
  contatoData?: IPAGEContacto | null;
}

const ContactForm: React.FC<ContactFormProps> = ({ contatoData }) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    apelido: '',
    email: '',
    telefone: '',
    assunto: '',
    mensagem: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Limpar mensagens de erro quando o usuário começar a digitar
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset status anterior
    setSubmitStatus('idle');
    setErrorMessage('');

    // Validação básica
    if (!formData.name || !formData.apelido || !formData.email || !formData.assunto || !formData.mensagem) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    setIsSubmitting(true);

    try {
      await createContato(formData);

      // Sucesso
      setSubmitStatus('success');

      // Reset do formulário após sucesso
      setFormData({
        name: '',
        apelido: '',
        email: '',
        telefone: '',
        assunto: '',
        mensagem: ''
      });

      // Limpar mensagem de sucesso após 5 segundos
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);

    } catch (error) {
      console.error('Erro ao enviar contato:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full relative bg-white mb-36 mt-30">
      <div className="flex flex-col lg:flex-row min-h-screen relative">
        {/* Left Side - Formulário (60% em desktop) */}
        <form onSubmit={handleSubmit} className="w-full lg:w-3/5 px-4 sm:px-6 py-8 md:py-12 lg:px-12 lg:py-16 flex flex-col overflow-y-auto lg:max-h-screen order-1">
          <div className='ml-0 lg:ml-16 max-w-full lg:max-w-[700px] bg-white rounded-2xl shadow-lg p-6 lg:absolute lg:top-1/2 lg:left-1/8 lg:transform lg:-translate-x-1/8 lg:-translate-y-1/2 z-10'>
            {/* Título Principal */}
          {contatoData?.title && (
            <div className="mb-6">
              {(() => {
                const sentence = contatoData?.title || "";
                const keywords = contatoData?.highlight_title || "";
                const positions = getHighlightPositions(sentence, keywords);
                return (
                  <HighlightedHeading
                    text={sentence}
                    positions={positions}
                  />
                );
              })()}
            </div>
          )}

          {/* Descrição */}
          {contatoData?.description && (
            <p className="text-slate-700 text-base font-light font-['Montserrat'] mb-8 leading-relaxed">
              {contatoData.description}
            </p>
          )}

          {/* Mensagens de Feedback */}
          {submitStatus === 'success' && (
            <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">Mensagem enviada com sucesso!</span>
              </div>
              <p className="mt-1 text-sm">Entraremos em contato em breve.</p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">Erro ao enviar mensagem</span>
              </div>
              <p className="mt-1 text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Formulário */}
         
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Nome"
              className="w-full h-12 px-5 bg-white border-b-1 border-gray-300 text-black text-base font-light font-['Montserrat'] placeholder-gray-500 focus:outline-none focus:border-[#22408B]"
              required
            />

            <input
              type="text"
              name="apelido"
              value={formData.apelido}
              onChange={handleInputChange}
              placeholder="Apelido"
              className="w-full h-12 px-5 bg-white border-b-1 border-gray-300 text-black text-base font-light font-['Montserrat'] placeholder-gray-500 focus:outline-none focus:border-[#22408B]"
              required
            />

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email"
              className="w-full h-12 px-5 bg-white border-b-1 border-gray-300 text-black text-base font-light font-['Montserrat'] placeholder-gray-500 focus:outline-none focus:border-[#22408B]"
              required
            />

            <input
              type="tel"
              name="telefone"
              value={formData.telefone}
              onChange={handleInputChange}
              placeholder="Telefone"
              className="w-full h-12 px-5 bg-white border-b-1 border-gray-300 text-black text-base font-light font-['Montserrat'] placeholder-gray-500 focus:outline-none focus:border-[#22408B]"
            />

            <input
              type="text"
              name="assunto"
              value={formData.assunto}
              onChange={handleInputChange}
              placeholder="Assunto"
              className="w-full h-12 px-5 bg-white border-b-1 border-gray-300 text-black text-base font-light font-['Montserrat'] placeholder-gray-500 focus:outline-none focus:border-[#22408B]"
              required
            />

            <textarea
              name="mensagem"
              value={formData.mensagem}
              onChange={handleInputChange}
              placeholder="Mensagem"
              rows={4}
              className="w-full px-5 py-3 bg-white border-b-1 border-gray-300 text-black text-base font-light font-['Montserrat'] placeholder-gray-500 focus:outline-none focus:border-[#22408B] resize-none"
              required
            />

            <div className="flex justify-start mt-6 lg:-mb-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`cursor-pointer rounded-full px-12 h-12 text-white text-lg font-medium font-['Montserrat'] uppercase transition-colors ${isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#E74C3C] hover:bg-[#C0392B]'
                  }`}
              >
                {isSubmitting ? 'Enviando...' : 'ENVIAR'}
              </button>
            </div>
          </div>
        </form>

        {/* Informações de contato no rodapé - posicionadas abaixo do formulário */}
        <div className="w-full lg:w-3/5 px-4 sm:px-6 lg:px-12 py-4 lg:py-8 order-3 lg:absolute lg:-bottom-6  lg:left-0">
          <div className="ml-0 lg:ml-26 xl:ml-42 flex flex-col sm:flex-row gap-3 sm:gap-6 flex-wrap">
            {contatoData?.adress && (
              <a href={`mailto:${contatoData.adress}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-900 transition-colors">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-normal font-['Montserrat'] break-all">{contatoData.adress}</span>
              </a>
            )}
            {contatoData?.telefone && (
              <a href={`tel:${contatoData.telefone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-900 transition-colors">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="text-sm font-normal font-['Montserrat']">{contatoData.telefone}</span>
              </a>
            )}
          </div>
        </div>

        {/* Right Side - Mapa Leaflet (40% em desktop) */}
        <div className="w-full lg:w-4/5 h-96 sm:h-[500px] md:h-[600px] lg:h-screen lg:sticky lg:top-0 overflow-hidden order-2">
          <LeafletMap
            coordenadas={contatoData?.long_latitude}
            localizacao={contatoData?.adress}
          />
        </div>
      </div>
    </div>
  );
};

export default ContactForm;